import React, { useState } from 'react';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import './FuelBalance.css';
import { useAuth } from '../../context/AuthContext';

const TABS = ['Daily Variance', 'Range Summary', 'Persisted Logs'];

export default function FuelBalance() {
  const { canAccess, can } = useAuth();
  const [tab, setTab] = useState('Daily Variance');

  if (!canAccess('fuel_balance')) {
    return (
      <div className="fuel-balance-page">
        <ErrBanner msg="Access Denied: You do not have permission to access fuel balance." />
      </div>
    );
  }

  return (
    <div className="fuel-balance-page">
      <div className="page-header">
        <h2 className="page-title">Fuel Balancing</h2>
      </div>

      <div className="fb-tabs">
        {TABS.map(t => (
          <button
            key={t}
            className={`fb-tab ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)}
          >{t}</button>
        ))}
      </div>

      <div className="fb-body">
        {tab === 'Daily Variance' && <DailyVariance />}
        {tab === 'Range Summary' && <RangeSummary />}
        {tab === 'Persisted Logs' && <PersistedLogs />}
      </div>
    </div>
  );
}

const fmt = (n) => fmtNum(n);
const alertPill = (level) => {
  const map = {
    ok: { txt: 'OK', cls: 'alert-ok' },
    warn: { txt: 'Warning', cls: 'alert-warn' },
    critical: { txt: 'Critical', cls: 'alert-crit' },
  };
  const info = map[level] || map.ok;
  return <span className={`alert-pill ${info.cls}`}>{info.txt}</span>;
};

/* ═══════════════════════════════════════════════════════════
   Daily variance — per-tank breakdown for a single date
═══════════════════════════════════════════════════════════ */
function DailyVariance() {
  const { can } = useAuth();
  const [date, setDate] = useState(today());
  const [savingId, setSavingId] = useState(null);
  const [msg, setMsg] = useState('');
  const { data, loading, error, reload } = useApi(
    () => api.fuelBalance.compute(date), [date],
    { tanks: [], total_expected: 0, total_actual: 0, total_variance: 0, worst_alert: 'ok' }
  );

  const persist = async (row) => {
    setSavingId(row.tank_id);
    try {
      await api.fuelBalance.logEntry({
        log_date: date,
        tank_id: row.tank_id,
        tank_no: row.tank_no,
        product_code: row.product_code,
        opening_qty: row.opening_qty,
        purchase_qty: row.purchase_qty,
        sales_qty: row.sales_qty,
        loss_qty: row.loss_qty,
        expected_qty: row.expected_qty,
        actual_qty: row.actual_qty,
        remarks: '',
      });
      setMsg(`✅ Saved variance log for ${row.tank_no}`);
      setTimeout(() => setMsg(''), 3500);
    } catch (e) {
      setMsg('❌ ' + e.message);
      setTimeout(() => setMsg(''), 4000);
    }
    setSavingId(null);
  };

  return (
    <div className="fb-section">
      <div className="fb-filters">
        <div className="form-group">
          <label>Date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <button className="btn btn-primary btn-sm" onClick={reload}>Refresh</button>
      </div>

      {msg && <div className="fb-toast">{msg}</div>}
      {error && <ErrBanner msg={error} />}

      {loading ? <Spinner /> : (
        <>
          <div className="fb-kpis">
            <Kpi label="Total Expected" value={`${fmt(data.total_expected)} L`} />
            <Kpi label="Total Actual (Dip)" value={`${fmt(data.total_actual)} L`} />
            <Kpi
              label="Net Variance"
              value={`${fmt(data.total_variance)} L`}
              tone={data.worst_alert === 'critical' ? 'red' : data.worst_alert === 'warn' ? 'orange' : 'green'}
            />
            <Kpi label="Alert Level" value={<span style={{ fontSize: 14 }}>{alertPill(data.worst_alert)}</span>} />
          </div>

          <div className="section-card" style={{ overflowX: 'auto' }}>
            <div className="section-header">Per-tank variance — {fmtDate(date)}</div>
            <table className="data-table fb-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Tank</th>
                  <th style={{ textAlign: 'left' }}>Product</th>
                  <th style={{ textAlign: 'right' }}>Opening</th>
                  <th style={{ textAlign: 'right' }}>+ Purchase</th>
                  <th style={{ textAlign: 'right' }}>− Sales</th>
                  <th style={{ textAlign: 'right' }}>− Loss</th>
                  <th style={{ textAlign: 'right' }}>= Expected</th>
                  <th style={{ textAlign: 'right' }}>Actual (Dip)</th>
                  <th style={{ textAlign: 'right' }}>Variance</th>
                  <th style={{ textAlign: 'right' }}>%</th>
                  <th>Alert</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.tanks.length === 0 ? (
                  <tr><td colSpan="12" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No active tanks</td></tr>
                ) : data.tanks.map(t => (
                  <tr key={t.tank_id} className={`row-${t.alert_level}`}>
                    <td style={{ textAlign: 'left', fontWeight: 700 }}>{t.tank_no}</td>
                    <td style={{ textAlign: 'left' }}>{t.product_code}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(t.opening_qty)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(t.purchase_qty)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(t.sales_qty)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(t.loss_qty)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(t.expected_qty)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {fmt(t.actual_qty)}
                      {!t.has_dip && <span className="tag-warn"> no dip</span>}
                    </td>
                    <td style={{
                      textAlign: 'right',
                      fontWeight: 700,
                      color: t.alert_level === 'critical' ? 'var(--danger)'
                        : t.alert_level === 'warn' ? '#d97706'
                          : 'var(--text-primary)',
                    }}>
                      {fmt(t.variance)}
                    </td>
                    <td style={{ textAlign: 'right' }}>{fmt(t.variance_pct || 0)}%</td>
                    <td>{alertPill(t.alert_level)}</td>
                    <td>
                      {can('fuel_balance', 'create') && (
                        <button
                          className="btn btn-sm"
                          onClick={() => persist(t)}
                          disabled={savingId === t.tank_id}
                        >
                          {savingId === t.tank_id ? '...' : '📝 Log'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="fb-legend">
            <span className="alert-pill alert-ok">OK</span> &lt; 0.5% variance &nbsp;·&nbsp;
            <span className="alert-pill alert-warn">Warn</span> 0.5–1.5% &nbsp;·&nbsp;
            <span className="alert-pill alert-crit">Critical</span> &gt; 1.5%
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Range summary — daily totals over up to 60 days
═══════════════════════════════════════════════════════════ */
function RangeSummary() {
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const { data, loading, error, reload } = useApi(
    () => api.fuelBalance.range(from, to), [from, to], { rows: [] }
  );

  // Set default range on first render: last 7 days
  React.useEffect(() => {
    const d = new Date();
    const prev = new Date(d.getTime() - 7 * 86400 * 1000);
    setFrom(prev.toISOString().slice(0, 10));
    setTo(d.toISOString().slice(0, 10));
    // eslint-disable-next-line
  }, []);

  return (
    <div className="fb-section">
      <div className="fb-filters">
        <div className="form-group">
          <label>From</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="form-group">
          <label>To</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <button className="btn btn-primary btn-sm" onClick={reload}>Refresh</button>
      </div>

      {error && <ErrBanner msg={error} />}

      {loading ? <Spinner /> : (
        <div className="section-card" style={{ overflowX: 'auto' }}>
          <div className="section-header">Daily totals — {fmtDate(from)} to {fmtDate(to)}</div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Date</th>
                <th style={{ textAlign: 'right' }}>Tanks</th>
                <th style={{ textAlign: 'right' }}>Expected (L)</th>
                <th style={{ textAlign: 'right' }}>Actual (L)</th>
                <th style={{ textAlign: 'right' }}>Variance (L)</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? (
                <tr><td colSpan="6" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No data</td></tr>
              ) : data.rows.map(r => (
                <tr key={r.date} className={`row-${r.alert_level}`}>
                  <td style={{ textAlign: 'left' }}>{fmtDate(r.date)}</td>
                  <td style={{ textAlign: 'right' }}>{r.tanks_count}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(r.total_expected)}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(r.total_actual)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(r.total_variance)}</td>
                  <td>{alertPill(r.alert_level)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Persisted logs — manager-acknowledged variance records
═══════════════════════════════════════════════════════════ */
function PersistedLogs() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  React.useEffect(() => {
    const d = new Date();
    const prev = new Date(d.getTime() - 30 * 86400 * 1000);
    setFrom(prev.toISOString().slice(0, 10));
    setTo(d.toISOString().slice(0, 10));
  }, []);

  const { data, loading, error, reload } = useApi(
    () => from && to ? api.fuelBalance.logs(from, to) : Promise.resolve({ rows: [] }),
    [from, to], { rows: [] }
  );

  return (
    <div className="fb-section">
      <div className="fb-filters">
        <div className="form-group">
          <label>From</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="form-group">
          <label>To</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <button className="btn btn-primary btn-sm" onClick={reload}>Refresh</button>
      </div>

      {error && <ErrBanner msg={error} />}

      {loading ? <Spinner /> : (
        <div className="section-card" style={{ overflowX: 'auto' }}>
          <div className="section-header">Variance logs ({data.rows.length})</div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Log Date</th>
                <th style={{ textAlign: 'left' }}>Tank</th>
                <th style={{ textAlign: 'left' }}>Product</th>
                <th style={{ textAlign: 'right' }}>Expected</th>
                <th style={{ textAlign: 'right' }}>Actual</th>
                <th style={{ textAlign: 'right' }}>Variance</th>
                <th style={{ textAlign: 'right' }}>%</th>
                <th>Alert</th>
                <th style={{ textAlign: 'left' }}>Logged At</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? (
                <tr><td colSpan="9" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No logged entries in this range</td></tr>
              ) : data.rows.map(r => (
                <tr key={r.id} className={`row-${r.alert_level}`}>
                  <td style={{ textAlign: 'left' }}>{fmtDate(r.log_date)}</td>
                  <td style={{ textAlign: 'left' }}>{r.tank_no}</td>
                  <td style={{ textAlign: 'left' }}>{r.product_code}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(r.expected_qty)}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(r.actual_qty)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(r.variance)}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(r.variance_pct || 0)}%</td>
                  <td>{alertPill(r.alert_level)}</td>
                  <td style={{ textAlign: 'left', fontSize: 11, color: 'var(--text-muted)' }}>{fmtDate(r.logged_at, true)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, tone }) {
  return (
    <div className={`fb-kpi ${tone ? 'kpi-' + tone : ''}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
    </div>
  );
}
